import { cn } from '@aibi/ui';
import type { KeyboardEvent, MouseEvent } from 'react';
import { conditionalStyleOf } from '../conditional-format';
import {
  channelColumns,
  effectiveFormat,
  formatValue,
  isNumericColumn,
  selectedValues,
  type IndexedColumn,
} from '../result-access';
import type { RendererProps } from '../types';
import { tableTextClassOf } from '../viz-style';

/** Filas visibles como maximo; el resto se indica en el pie (sin virtualizacion todavia). */
const MAX_VISIBLE_ROWS = 200;

/**
 * Tabla analitica. Las celdas de dimension son botones: clic selecciona (Ctrl/Cmd para
 * ampliar), doble clic profundiza en la jerarquia.
 */
export function TableRenderer({
  spec,
  result,
  selections,
  density,
  label,
  onSelect,
  onDrill,
}: RendererProps) {
  const encoded = [
    ...channelColumns(spec, result, 'columns'),
    ...channelColumns(spec, result, 'rows'),
    ...channelColumns(spec, result, 'values'),
  ];
  const columns: IndexedColumn[] =
    encoded.length > 0 ? encoded : result.columns.map((column, index) => ({ column, index }));
  const rows = result.rows.slice(0, MAX_VISIBLE_ROWS);
  const grandTotal = result.totals?.grandTotal;
  const rowHeight = density === 'compact' ? 'h-row-compact' : 'h-row-comfortable';
  const textClass = tableTextClassOf(spec.style);

  const emit = (
    handler: RendererProps['onSelect'],
    entry: IndexedColumn,
    row: readonly (string | number | boolean | null)[],
    event: MouseEvent | KeyboardEvent,
  ) => {
    handler?.({
      columnKey: entry.column.key,
      fieldId: entry.column.fieldId,
      value: row[entry.index] ?? null,
      additive: event.ctrlKey || event.metaKey,
    });
  };

  return (
    <div className="h-full overflow-auto" data-testid="table-renderer">
      <table className={cn('w-full border-separate border-spacing-0', textClass)} aria-label={label}>
        <thead className="sticky top-0 z-10 bg-card">
          <tr>
            {columns.map(({ column }) => (
              <th
                key={column.key}
                scope="col"
                className={cn(
                  'border-b border-border px-3 py-2 font-medium text-muted-foreground',
                  isNumericColumn(column) ? 'text-right' : 'text-left',
                )}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, r) => (
            <tr key={r} className="group">
              {columns.map((entry) => {
                const { column, index } = entry;
                const value = row[index] ?? null;
                const text = formatValue(value, column, effectiveFormat(spec, column));
                const numeric = isNumericColumn(column);
                const conditional = conditionalStyleOf(spec, column.key, value);
                if (column.role !== 'dimension' || !spec.interaction.selectable) {
                  return (
                    <td
                      key={column.key}
                      className={cn(
                        rowHeight,
                        'border-b border-border/60 px-3 group-hover:bg-accent/50',
                        numeric && 'text-right tabular-nums',
                      )}
                      style={conditional}
                    >
                      {text}
                    </td>
                  );
                }
                const selected = selectedValues(selections, column).includes(value);
                return (
                  <td
                    key={column.key}
                    className={cn(
                      rowHeight,
                      'border-b border-border/60 group-hover:bg-accent/50',
                      selected && 'bg-selection-muted',
                    )}
                    style={conditional}
                  >
                    <button
                      type="button"
                      aria-pressed={selected}
                      title="Clic: seleccionar. Ctrl+clic: agregar. Doble clic: profundizar."
                      className={cn(
                        'h-full w-full px-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                        selected && 'font-medium text-selection',
                      )}
                      onClick={(event) => {
                        emit(onSelect, entry, row, event);
                      }}
                      onDoubleClick={(event) => {
                        if (spec.interaction.drillOnClick) emit(onDrill, entry, row, event);
                      }}
                    >
                      {text}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        {grandTotal && (
          <tfoot className="sticky bottom-0 bg-card">
            <tr>
              {columns.map(({ column, index }, i) => (
                <td
                  key={column.key}
                  className={cn(
                    rowHeight,
                    'border-t border-border px-3 font-semibold',
                    isNumericColumn(column) && 'text-right tabular-nums',
                  )}
                >
                  {column.role === 'dimension'
                    ? i === 0
                      ? 'Total'
                      : ''
                    : formatValue(grandTotal[index] ?? null, column, effectiveFormat(spec, column))}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
      {result.rows.length > MAX_VISIBLE_ROWS && (
        <p className="px-3 py-2 text-xs text-muted-foreground">
          Mostrando {MAX_VISIBLE_ROWS} de {result.rows.length} filas.
        </p>
      )}
    </div>
  );
}
